import {AgmCoreModule} from '@agm/core';
import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {RouterModule} from '@angular/router';
import {NgxPaginationModule} from 'ngx-pagination';
import {environment} from '../../environments/environment';
import {RequestPageComponent} from '../core/request-page/request-page.component';
import {ProfileModule} from '../profile/profile.module';
import {SharedModule} from '../shared/shared.module';
import {BadgeComponent} from './badge/badge.component';
import {CandidateProfilePageComponent} from './candidate-profile-page/candidate-profile-page.component';
import {CandidateSummaryComponent} from './candidate-summary/candidate-summary.component';
import {ClientApiService} from './client-api.service';
import {ContactAgentComponent} from './contact-agent/contact-agent.component';
import {CtaComponent} from './cta/cta.component';
import {HomePageComponent} from './home-page/home-page.component';
import {InvoiceFilterSortPipe} from './invoice-filter-sort.pipe';
import {InvoiceSummaryComponent} from './invoice-summary/invoice-summary.component';
import {InvoicesPageComponent} from './invoices-page/invoices-page.component';
import {LinkTypePipe} from './link-type.pipe';
import {NewRoleFormComponent} from './new-role-form/new-role-form.component';
import {NotesPanelComponent} from './notes-panel/notes-panel.component';
import {PageControlsComponent} from './page-controls/page-controls.component';
import {PdfModalComponent} from './pdf-modal/pdf-modal.component';
import {GooglePlacesComponent} from './places-autocomplete/places-autocomplete.component';
import {PersonaComponent} from './persona-promotion/persona-promotion.component';
import {PoolsPageComponent} from './pools-page/pools-page.component';
import {PoolsPipe} from './pools.pipe';
import {PositionActivityComponent} from './position-activity/position-activity.component';
import {PositionCandidatesPageComponent} from './position-candidates-page/position-candidates-page.component';
import {PositionDetailsPageComponent} from './position-details-page/position-details-page.component';
import {PositionSearchPipe} from './position-search.pipe';
import {PositionSummaryDecoyComponent} from './position-summary-decoy/position-summary-decoy.component';
import {PositionSummaryComponent} from './position-summary/position-summary.component';
import {PositionsPageDecoyComponent} from './positions-page-decoy/positions-page-decoy.component';
import {PositionsPageMockHeaderComponent} from './positions-page-mock-header/positions-page-mock-header.component';
import {PositionsPageComponent} from './positions-page/positions-page.component';
import {QccModalComponent} from './qcc-modal/qcc-modal.component';
import {QccMultiModalComponent} from './qcc-multi-modal/qcc-multi-modal.component';
import {QccSuccessModalComponent} from './qcc-success-modal/qcc-success-modal.component';
import {QccGuard} from './qcc.guard';
import {ReportsPageComponent} from './reports-page/reports-page.component';
import {SalaryGuideComponent} from './salary-guide/salary-guide.component';
import {AquentsBookPromotionComponent} from './aquents-book-promotion/aquents-book-promotion.component';
import {TalentCardPipe} from './talent-card.pipe';
import {TalentPoolCardComponent} from './talent-pool-card/talent-pool-card.component';
import {TalentPoolComponent} from './talent-pool/talent-pool.component';
import {TermsModalComponent} from './terms-modal/terms-modal.component';
import {TimecardDetailsPageComponent} from './timecard-details-page/timecard-details-page.component';
import {TimecardDetailsTableDailyRateComponent} from './timecard-details-table-daily-rate/timecard-details-table-daily-rate.component';
import {TimecardDetailsTableDtrComponent} from './timecard-details-table-dtr/timecard-details-table-dtr.component';
import {TimecardDetailsTableComponent} from './timecard-details-table/timecard-details-table.component';
import {TimecardFilterSortPipe} from './timecard-filter-sort.pipe';
import {TimecardHalfFullDayFormatPipe} from './timecard-half-full-day-format.pipe';
import {TimecardHoursFormatPipe} from './timecard-hours-format.pipe';
import {TimecardIntervalPipe} from './timecard-interval.pipe';
import {TimecardSignComponent} from './timecard-sign/timecard-sign.component';
import {TimecardStatusPipe} from './timecard-status.pipe';
import {TimecardSummaryComponent} from './timecard-summary/timecard-summary.component';
import {TimecardTimePipe} from './timecard-time.pipe';
import {TimecardsPageComponent} from './timecards-page/timecards-page.component';
import {TrackerActivityPipe} from './tracker-activity.pipe';
import {TrackerHistoryComponent} from './tracker-history/tracker-history.component';
import {TrackerStatusPipe} from './tracker-status.pipe';
import {UpdateAssignmentModalComponent} from './update-assignment-modal/update-assignment-modal.component';
import {DiversityInsightService} from './diversity-insight/service/diversity-insight.service';

const PIPES = [
  LinkTypePipe,
  TalentCardPipe,
  TrackerStatusPipe,
  PositionSearchPipe,
  TrackerActivityPipe,
  PoolsPipe,
  TimecardHoursFormatPipe,
  TimecardHalfFullDayFormatPipe,
  TimecardStatusPipe,
  TimecardTimePipe,
  TimecardIntervalPipe,
  TimecardFilterSortPipe,
  InvoiceFilterSortPipe,
  GooglePlacesComponent
];

/**
 * Provides client-facing endpoints.
 */
@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    ProfileModule,
    NgxPaginationModule,
    AgmCoreModule.forRoot({
      apiKey: environment.googleApiKey,
      libraries: ['places']
    })
  ],
  declarations: [
    CandidateProfilePageComponent,
    CandidateSummaryComponent,
    ContactAgentComponent,
    CtaComponent,
    PositionCandidatesPageComponent,
    TrackerHistoryComponent,
    NotesPanelComponent,
    PositionsPageComponent,
    PositionsPageDecoyComponent,
    PositionSummaryComponent,
    PositionSummaryDecoyComponent,
    PositionsPageMockHeaderComponent,
    PoolsPageComponent,
    TimecardsPageComponent,
    TimecardSummaryComponent,
    TimecardDetailsPageComponent,
    TimecardDetailsTableComponent,
    TimecardDetailsTableDtrComponent,
    TimecardDetailsTableDailyRateComponent,
    TimecardSignComponent,
    BadgeComponent,
    PositionDetailsPageComponent,
    QccModalComponent,
    QccMultiModalComponent,
    QccSuccessModalComponent,
    InvoicesPageComponent,
    InvoiceSummaryComponent,
    TalentPoolCardComponent,
    TalentPoolComponent,
    HomePageComponent,
    PageControlsComponent,
    PersonaComponent,
    PdfModalComponent,
    TermsModalComponent,
    SalaryGuideComponent,
    AquentsBookPromotionComponent,
    ReportsPageComponent,
    RequestPageComponent,
    NewRoleFormComponent,
    PositionActivityComponent,
    UpdateAssignmentModalComponent,
    ...PIPES
  ],
  providers: [
    ClientApiService,
    QccGuard,
    DiversityInsightService,
    ...PIPES
  ],
  exports: [RouterModule]
})
export class ClientModule {
}
